import { LinkAction } from '@atlas/ui-components'
import { ArrowLeft } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'

export interface BackLinkProps {
	/** Where to land when there is no in-app history — the list this record belongs to. */
	fallbackHref: string
	className?: string
}

/**
 * Back to wherever the reader came from.
 *
 * A detail page is reachable from several places - a list, a search result, a card on
 * another record - so naming one destination is wrong for every reader who arrived from
 * a different one. The router labels the first entry of a session `default`: anything
 * else means there is somewhere in-app to return to, and only a cold landing falls back
 * to the list.
 *
 * Rendered as a real anchor on the fallback, so cmd-click still opens that list in a new
 * tab while a plain click steps back.
 */
export function BackLink({ fallbackHref, className }: BackLinkProps) {
	const navigate = useNavigate()
	const hasInAppHistory = useLocation().key !== 'default'

	return (
		<LinkAction
			href={fallbackHref}
			onNavigate={() => void (hasInAppHistory ? navigate(-1) : navigate(fallbackHref))}
			className={className ?? 'inline-flex items-center gap-1 text-ds-sm text-ds-text-muted hover:text-ds-text'}
		>
			<ArrowLeft aria-hidden className="h-4 w-4" /> Back
		</LinkAction>
	)
}
